import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import apiClient, { getErrorMessage } from '../../utils/api';
import { Attendance as AttendanceType, AttendanceStatus, Employee } from '../../types/api';
import { PlusIcon, PencilIcon, XMarkIcon, ExclamationTriangleIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface AttendanceFormData {
  employee_id: string;
  attendance_date: string;
  status: AttendanceStatus;
  daily_wage_rate?: number;
  remarks?: string;
  business_type: 'fish' | 'goat';
}

const attendanceSchema = yup.object({
  employee_id: yup.string().required('Employee is required'),
  attendance_date: yup.string().required('Date is required'),
  status: yup.string().oneOf(['present', 'absent', 'half_day']).required('Status is required'),
  daily_wage_rate: yup.number().positive('Rate must be positive').optional(),
  remarks: yup.string().optional(),
  business_type: yup.string().oneOf(['fish', 'goat']).required('Business type is required'),
});

const AttendancePage: React.FC = () => {
  const { selectedLocation } = useLocationContext();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attendances, setAttendances] = useState<AttendanceType[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [dailyWageEmployees, setDailyWageEmployees] = useState<Employee[]>([]);
  const [missingAttendance, setMissingAttendance] = useState<Array<{ employee_id: string; employee_name: string; employee_code: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<AttendanceType | null>(null);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  // Set default date range to last 7 days
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.startDate);
  const [endDate, setEndDate] = useState<string>(defaultDates.endDate);
  const fetchingRef = useRef<{ employees: boolean; missing: boolean; attendances: boolean }>({
    employees: false,
    missing: false,
    attendances: false
  });
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  const form = useForm<AttendanceFormData>({
    resolver: yupResolver(attendanceSchema) as any,
    defaultValues: {
      employee_id: '',
      attendance_date: new Date().toISOString().split('T')[0],
      status: 'present',
      remarks: '',
      business_type: businessType,
    },
  });

  const fetchEmployees = useCallback(async () => {
    if (!selectedLocation || fetchingRef.current.employees) return;
    fetchingRef.current.employees = true;
    try {
      // Fetch all active employees regardless of business type
      const response = await apiClient.get<Employee[]>(`/employees?location_id=${selectedLocation.id}&is_active=true`);
      setEmployees(response.data);
      setDailyWageEmployees(response.data.filter(e => e.employee_type === 'daily_wage_labour'));
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      fetchingRef.current.employees = false;
    }
  }, [selectedLocation]); // Removed businessType dependency to show all business types

  const fetchMissingAttendance = useCallback(async () => {
    if (!selectedLocation || !isManagerOrAdmin || fetchingRef.current.missing) return;
    fetchingRef.current.missing = true;
    try {
      const response = await apiClient.get<{ missing_attendance: Array<{ employee_id: string; employee_name: string; employee_code: string }> }>(`/attendances/missing?location_id=${selectedLocation.id}`).catch(err => {
        console.error('Error fetching missing attendance:', err);
        return { data: { missing_attendance: [] } };
      });
      setMissingAttendance(response.data.missing_attendance || []);
    } catch (error) {
      console.error('Error fetching missing attendance:', error);
    } finally {
      fetchingRef.current.missing = false;
    }
  }, [selectedLocation, isManagerOrAdmin]);

  const fetchAttendances = useCallback(async () => {
    if (!selectedLocation || !startDate || !endDate || fetchingRef.current.attendances) return;
    fetchingRef.current.attendances = true;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        location_id: selectedLocation.id,
        start_date: startDate,
        end_date: endDate,
        ...(selectedEmployee && { employee_id: selectedEmployee }),
      });
      const response = await apiClient.get<AttendanceType[]>(`/attendances?${params.toString()}`);
      setAttendances(response.data);
    } catch (error) {
      console.error('Error fetching attendances:', error);
    } finally {
      setLoading(false);
      fetchingRef.current.attendances = false;
    }
  }, [selectedLocation, startDate, endDate, selectedEmployee]);

  useEffect(() => {
    if (selectedLocation) {
      // Fetch employees and missing attendance in parallel, but only once
      Promise.allSettled([
        fetchEmployees(),
        fetchMissingAttendance()
      ]).catch(err => console.error('Error fetching initial data:', err));
    }
  }, [selectedLocation, fetchEmployees, fetchMissingAttendance]);

  useEffect(() => {
    if (selectedLocation && startDate && endDate) {
      // Debounce the attendance fetch to avoid rapid successive calls
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        fetchAttendances();
      }, 300); // 300ms debounce
      
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }
  }, [selectedLocation, startDate, endDate, selectedEmployee, fetchAttendances]);

  const handleSubmit = async (data: AttendanceFormData) => {
    if (!selectedLocation) {
      setFormError('Please select a location');
      return;
    }

    setFormError('');
    setSubmitting(true);

    try {
      if (editingAttendance) {
        // Update attendance
        await apiClient.put(`/attendances/${editingAttendance.id}`, {
          status: data.status,
          remarks: data.remarks || undefined,
        });
      } else {
        // Create attendance
        await apiClient.post('/attendances', {
          employee_id: data.employee_id,
          location_id: selectedLocation.id,
          business_type: data.business_type,
          attendance_date: data.attendance_date,
          status: data.status,
          remarks: data.remarks || undefined,
        });
      }

      setShowForm(false);
      setEditingAttendance(null);
      form.reset();
      fetchAttendances();
      fetchMissingAttendance();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to save attendance');
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (attendance: AttendanceType) => {
    setEditingAttendance(attendance);
    const employee = employees.find(e => e.id === attendance.employee_id);
    form.reset({
      employee_id: attendance.employee_id,
      attendance_date: attendance.attendance_date.split('T')[0],
      status: attendance.status,
      remarks: attendance.remarks || '',
      business_type: employee?.business_type || businessType,
    });
    setShowForm(true);
  };

  const handleQuickMark = async (employeeId: string, status: AttendanceStatus) => {
    if (!selectedLocation) return;
    
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) return;
    
    try {
      await apiClient.post('/attendances', {
        employee_id: employeeId,
        location_id: selectedLocation.id,
        business_type: employee.business_type,
        attendance_date: new Date().toISOString().split('T')[0],
        status: status,
      });
      fetchAttendances();
      fetchMissingAttendance();
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to mark attendance'));
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAttendance(null);
    setFormError('');
    form.reset();
  };

  const handleDelete = async (attendanceId: string) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;
    try {
      await apiClient.delete(`/attendances/${attendanceId}`);
      fetchAttendances();
      fetchMissingAttendance();
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to delete attendance'));
    }
  };

  // Memoize employee lookup map for better performance
  const employeeMap = useMemo(() => {
    const map = new Map<string, Employee>();
    employees.forEach(emp => map.set(emp.id, emp));
    return map;
  }, [employees]);

  const getStatusColor = (status: AttendanceStatus) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800';
      case 'absent':
        return 'bg-red-100 text-red-800';
      case 'half_day':
        return 'bg-yellow-100 text-yellow-800';
      case 'leave':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if attendance can be edited/deleted based on date
  // Managers can only edit/delete attendance from today or yesterday
  // Admins can always edit/delete
  const canEditOrDeleteByDate = (attendanceDate: string): boolean => {
    if (isAdmin) {
      return true; // Admins can always edit/delete
    }
    
    if (user?.role !== 'manager') {
      return false; // Only managers and admins can edit/delete
    }
    
    // Extract date part from attendance date string (handle both ISO and date-only formats)
    let attDateStr: string;
    if (attendanceDate.includes('T')) {
      // ISO string with time: "2025-12-23T00:00:00" or "2025-12-23T00:00:00Z"
      attDateStr = attendanceDate.split('T')[0];
    } else {
      // Date-only string: "2025-12-23"
      attDateStr = attendanceDate;
    }
    
    // Get today and yesterday as date strings (YYYY-MM-DD format)
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    // Compare date strings directly to avoid timezone issues
    return attDateStr === todayStr || attDateStr === yesterdayStr;
  };

  if (!selectedLocation) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a location to view attendance.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/payroll')}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          <span>Back to Payroll</span>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Management</h1>
          <p className="text-gray-600 mt-1">Mark and view employee attendance</p>
        </div>
      </div>

      {/* Missing Attendance Alert */}
      {missingAttendance.length > 0 && isManagerOrAdmin && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">
                Missing Attendance for Today ({missingAttendance.length} employee{missingAttendance.length > 1 ? 's' : ''})
              </h3>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                {missingAttendance.map((emp) => (
                  <div key={emp.employee_id} className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 bg-white px-3 py-2 rounded border border-yellow-200">
                    <span className="text-sm text-gray-700 font-medium mb-2 sm:mb-0">{emp.employee_name}</span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleQuickMark(emp.employee_id, 'present')}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      >
                        Present
                      </button>
                      <button
                        onClick={() => handleQuickMark(emp.employee_id, 'half_day')}
                        className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                      >
                        Half Day
                      </button>
                      <button
                        onClick={() => handleQuickMark(emp.employee_id, 'absent')}
                        className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                      >
                        Absent
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Employees</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} ({emp.employee_code})
                </option>
              ))}
            </select>
          </div>
          {isManagerOrAdmin && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  form.reset({
                    employee_id: '',
                    attendance_date: endDate,
                    status: 'present',
                    remarks: '',
                  });
                  setShowForm(true);
                }}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Mark Attendance</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && attendances.length === 0 ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-2">
            {attendances.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-4 text-center text-gray-500">
                <p className="text-sm">No attendance records found for the selected date range.</p>
              </div>
            ) : (
              attendances.map((att) => {
                const employee = employeeMap.get(att.employee_id);
                const isRegularEmployee = employee?.employee_type === 'manager' || employee?.employee_type === 'regular_farm_labour';
                const canEditByEmployeeType = isManagerOrAdmin && (!isRegularEmployee || user?.role === 'admin');
                const canEditByDate = canEditOrDeleteByDate(att.attendance_date);
                const canEdit = canEditByEmployeeType && canEditByDate;
                const canDelete = isAdmin || (user?.role === 'manager' && canEditByDate);
                
                return (
                  <div key={att.id} className="bg-white rounded-lg shadow p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0 pr-2">
                        <h3 className="text-base font-semibold text-gray-900 mb-0.5 truncate">
                          {att.employee_name || 'N/A'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {new Date(att.attendance_date).toLocaleDateString('en-IN', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1.5 flex-shrink-0">
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${getStatusColor(att.status)}`}>
                          {att.status === 'half_day' ? 'Half Day' : att.status.charAt(0).toUpperCase() + att.status.slice(1)}
                        </span>
                        {att.is_auto_generated && (
                          <span className="text-xs text-gray-400">Auto</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-1 text-xs mb-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Business Type:</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          att.business_type === 'fish' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {att.business_type === 'fish' ? 'Fish' : 'Goat'}
                        </span>
                      </div>
                      {att.location_name && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Location:</span>
                          <span className="text-gray-900 truncate ml-2">{att.location_name}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Daily Wage Rate:</span>
                        <span className="text-gray-900 font-medium">
                          {att.daily_wage_rate ? `₹${att.daily_wage_rate.toLocaleString('en-IN')}` : '-'}
                        </span>
                      </div>
                      {(att.check_in_time || att.check_out_time) && (
                        <div className="flex flex-col">
                          <span className="text-gray-500 mb-0.5">Check-in/Check-out:</span>
                          <div className="text-gray-900">
                            {att.check_in_time && (
                              <div className="text-xs">In: {new Date(att.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                            )}
                            {att.check_out_time && (
                              <div className="text-xs">Out: {new Date(att.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                            )}
                          </div>
                        </div>
                      )}
                      {att.remarks && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Remarks:</span>
                          <span className="text-gray-900 truncate ml-2">{att.remarks}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type:</span>
                        <span className="text-gray-900">
                          {att.is_auto_generated ? 'Auto-generated' : 'Manual'}
                        </span>
                      </div>
                    </div>
                    
                    {isManagerOrAdmin && (
                      <div className="flex space-x-1.5 pt-2 border-t">
                        {canEdit && (
                          <button
                            onClick={() => handleEdit(att)}
                            className="text-blue-600 hover:text-blue-900 p-1.5 border border-blue-300 rounded hover:bg-blue-50"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(att.id)}
                            className="text-red-600 hover:text-red-900 p-1.5 border border-red-300 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Wage Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-in/Check-out</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    {isManagerOrAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendances.map((att) => {
                    const employee = employees.find(e => e.id === att.employee_id);
                    const isRegularEmployee = employee?.employee_type === 'manager' || employee?.employee_type === 'regular_farm_labour';
                    const canEditByEmployeeType = isManagerOrAdmin && (!isRegularEmployee || user?.role === 'admin');
                    const canEditByDate = canEditOrDeleteByDate(att.attendance_date);
                    const canEdit = canEditByEmployeeType && canEditByDate;
                    const canDelete = isAdmin || (user?.role === 'manager' && canEditByDate);
                    
                    return (
                      <tr key={att.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {att.employee_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            att.business_type === 'fish' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {att.business_type === 'fish' ? 'Fish' : 'Goat'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {att.location_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(att.attendance_date).toLocaleDateString('en-IN', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(att.status)}`}>
                            {att.status === 'half_day' ? 'Half Day' : att.status.charAt(0).toUpperCase() + att.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {att.daily_wage_rate ? `₹${att.daily_wage_rate.toLocaleString('en-IN')}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {att.check_in_time || att.check_out_time ? (
                            <div className="text-xs">
                              {att.check_in_time && (
                                <div>In: {new Date(att.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                              )}
                              {att.check_out_time && (
                                <div>Out: {new Date(att.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={att.remarks || undefined}>
                          {att.remarks || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {att.is_auto_generated ? (
                            <span className="text-xs text-gray-400">Auto-generated</span>
                          ) : (
                            <span className="text-xs text-gray-400">Manual</span>
                          )}
                        </td>
                        {isManagerOrAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              {canEdit && (
                                <button
                                  onClick={() => handleEdit(att)}
                                  className="text-blue-600 hover:text-blue-900 transition-colors"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDelete(att.id)}
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              )}
                              {!canEdit && !canDelete && (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {attendances.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No attendance records found for the selected date range.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Attendance Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {editingAttendance ? 'Edit Attendance' : 'Mark Attendance'}
              </h2>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{formError}</p>
              </div>
            )}

            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('employee_id')}
                    disabled={!!editingAttendance}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.employee_id ? 'border-red-500' : 'border-gray-300'
                    } ${editingAttendance ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select employee</option>
                    {dailyWageEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_code})
                      </option>
                    ))}
                  </select>
                  {form.formState.errors.employee_id && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.employee_id.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Only daily wage labour employees can be marked manually</p>
                </div>

                {/* Business Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('business_type')}
                    disabled={!!editingAttendance}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.business_type ? 'border-red-500' : 'border-gray-300'
                    } ${editingAttendance ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="fish">Fish</option>
                    <option value="goat">Goat</option>
                  </select>
                  {form.formState.errors.business_type && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.business_type.message}</p>
                  )}
                  {editingAttendance && (
                    <p className="mt-1 text-xs text-gray-500">Business type cannot be changed after creation</p>
                  )}
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...form.register('attendance_date')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.attendance_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {form.formState.errors.attendance_date && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.attendance_date.message}</p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('status')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.status ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="present">Present</option>
                    <option value="half_day">Half Day</option>
                    <option value="absent">Absent</option>
                  </select>
                  {form.formState.errors.status && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.status.message}</p>
                  )}
                </div>

                {/* Remarks */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remarks
                  </label>
                  <textarea
                    {...form.register('remarks')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional remarks"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>{editingAttendance ? 'Update Attendance' : 'Mark Attendance'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
